import { Metamodel } from 'app/model/base/model';
import { SapOdpCopySource } from 'app/model/pipeline/copysource/sap-odp.copysource';
import { AdapterRegistry, IModel, Owned } from 'shell-models-lib';
import { commonCopySourceAdapts, CopySourceAdapter } from './copysource.adapter';
import { Model } from 'app/model/backendmodel';
import { ValueAdapter } from '../../value.adapter';
import { CopyActivity } from 'app/model/pipeline/copy.activity';
import { SapOdpSelection } from 'app/model/pipeline/copysource/sap-odp-selection';

@AdapterRegistry.adapter({
    model: Metamodel.modelFor(SapOdpCopySource),
    asynchronous: true,
    adapts: {
        ...commonCopySourceAdapts,
        extractionMode: '*',
        subscriberProcess: '*',
        projection: '*',
        selection: '*'
    }
})
export class SapOdpCopySourceAdapter extends CopySourceAdapter<SapOdpCopySource, Model.SapOdpSource> {
    protected model(): IModel {
        return SapOdpCopySource.MODEL;
    }

    protected override async _toResource(copySource: SapOdpCopySource): Promise<Model.SapOdpSource> {
        const result: Model.SapOdpSource = this.getCommonCopySourceProperties(copySource, this.model().name);
        result.extractionMode = ValueAdapter.toResource(copySource, SapOdpCopySource.EXTRACTION_MODE);
        result.subscriberProcess =
            ValueAdapter.toResource(copySource, SapOdpCopySource.SUBSCRIBER_PROCESS) || undefined;
        result.projection = ValueAdapter.toResource(copySource, SapOdpCopySource.PROJECTION);
        result.selection = ValueAdapter.toResource(copySource, SapOdpCopySource.SELECTION);
        if (Array.isArray(result.selection)) {
            const selection = result.selection;
            result.selection = selection.map((value) => ({
                fieldName: value.fieldName,
                sign: value.sign,
                option: value.option,
                low: ValueAdapter.toResource(value, SapOdpSelection.LOW),
                high: ValueAdapter.toResource(value, SapOdpSelection.HIGH) || undefined
            }));
        }
        if (Array.isArray(result.projection) && !result.projection?.length) {
            Reflect.deleteProperty(result, 'projection');
        }
        if (Array.isArray(result.selection) && !result.selection?.length) {
            Reflect.deleteProperty(result, 'selection');
        }
        return result;
    }

    protected override async _toModel(
        resource: Model.SapOdpSource,
        copyActivity: CopyActivity
    ): Promise<SapOdpCopySource> {
        const result = this.createCopySourceWithCommonProperties(
            this.model(),
            resource,
            copyActivity
        ) as SapOdpCopySource;
        result.mode = 'table'; // only support table
        ValueAdapter.toModel(result, SapOdpCopySource.EXTRACTION_MODE, resource.extractionMode);
        ValueAdapter.toModel(result, SapOdpCopySource.SUBSCRIBER_PROCESS, resource.subscriberProcess);
        ValueAdapter.toModel(result, SapOdpCopySource.PROJECTION, resource.projection);
        ValueAdapter.toModel(result, SapOdpCopySource.SELECTION, resource.selection);
        if (Array.isArray(result.selection)) {
            const selection = result.selection;
            result.selection = selection.map((value) => {
                const sapOdpSelection = Owned.create<SapOdpSelection>(SapOdpSelection.MODEL, result?.owner);
                const { fieldName, sign, option, low, high } = value;
                sapOdpSelection.fieldName = fieldName;
                sapOdpSelection.sign = sign;
                sapOdpSelection.option = option;
                ValueAdapter.toModel(sapOdpSelection, SapOdpSelection.LOW, low);
                ValueAdapter.toModel(sapOdpSelection, SapOdpSelection.HIGH, high);
                return sapOdpSelection;
            });
        }
        return result;
    }
}
